import Phaser from "phaser";
import { GAME_WIDTH } from "../constants";
import { addCoverImage, addPanel } from "../ui/chrome";
import { MenuButton, bindMenuKeys } from "../ui/MenuButton";
import { bodyStyle, titleStyle } from "../ui/theme";
import { defaultInitialsArray, normalizeInitials, replaceInitialAt, stepLetter } from "../logic/initials";
import { previewQualification, submitHighScore } from "../services/leaderboardClient";
import { audioManager } from "../systems/AudioManager";
import { assetOrigin } from "../assets/loader";

interface ResultsData {
  score: number;
  completed: boolean;
}

export class ResultsScene extends Phaser.Scene {
  private score = 0;
  private letters: [string, string, string] = defaultInitialsArray();
  private slot = 0;
  private slotTexts: Phaser.GameObjects.Text[] = [];
  private status!: Phaser.GameObjects.Text;
  private pendingScore: number | null = null;
  private submitting = false;
  private replay!: MenuButton;
  private menu!: MenuButton;
  private unbindNav: (() => void) | null = null;

  constructor() {
    super("Results");
  }

  init(data: ResultsData): void {
    this.score = data.score;
    this.pendingScore = data.score;
    this.letters = defaultInitialsArray();
    this.slot = 0;
    this.submitting = false;
    this.unbindNav = null;
  }

  create(): void {
    audioManager.playMusic("menu");
    addCoverImage(this, "menu-background");
    addPanel(this, GAME_WIDTH / 2, 360, 820, 620);

    const bear = this.add.sprite(980, 430, "bear-celebrate", 0);
    const origin = assetOrigin("bear-celebrate");
    bear.setOrigin(origin.x, origin.y).setScale(0.78).play("bear-celebrate");

    this.add.text(GAME_WIDTH / 2, 110, "Cave reached!", titleStyle(44)).setOrigin(0.5).setColor("#2a1a10").setStroke("#000000", 0);
    this.add.text(GAME_WIDTH / 2, 170, `Score  ${this.score}`, titleStyle(32)).setOrigin(0.5).setColor("#5c3a1e").setStroke("#000000", 0);

    this.status = this.add.text(GAME_WIDTH / 2, 230, "Checking the high-score trail…", bodyStyle(20)).setOrigin(0.5);

    this.replay = new MenuButton(this, GAME_WIDTH / 2 - 118, 580, "Play Again", () => this.scene.start("Game"), 200);
    this.menu = new MenuButton(this, GAME_WIDTH / 2 + 118, 580, "Menu", () => this.scene.start("Menu"), 200, "secondary");
    this.replay.setEnabled(false);
    this.menu.setEnabled(false);

    void this.checkQualification();
  }

  private async checkQualification(): Promise<void> {
    const preview = await previewQualification(this.score);
    if (!this.scene.isActive()) {
      return;
    }
    if (!preview.qualifies) {
      this.status.setText(
        preview.source === "local-fallback"
          ? "Local scores only (not production data). This run is finished!"
          : "A cozy finish — not quite a top-10 this time.",
      );
      this.enableNav();
      return;
    }
    this.status.setText("New high score! Enter your initials.");
    this.buildInitials();
    this.playCelebrateFx();
  }

  private enableNav(): void {
    this.replay.setEnabled(true);
    this.menu.setEnabled(true);
    this.unbindNav?.();
    this.unbindNav = bindMenuKeys(this, [this.replay, this.menu]);
  }

  private buildInitials(): void {
    const frame = this.add.image(GAME_WIDTH / 2, 330, "initials-selector");
    frame.setDisplaySize(420, 132);
    this.slotTexts = this.letters.map((letter, index) => {
      const text = this.add.text(GAME_WIDTH / 2 - 110 + index * 110, 328, letter, titleStyle(48));
      text.setOrigin(0.5).setColor("#2a1a10").setStroke("#000000", 0);
      return text;
    });
    this.highlightSlot();

    new MenuButton(this, GAME_WIDTH / 2 - 118, 450, "Submit", () => {
      void this.submit();
    }, 200);
    new MenuButton(this, GAME_WIDTH / 2 + 118, 450, "Skip", () => this.skipSave(), 200, "secondary");
    this.add.text(GAME_WIDTH / 2, 508, "Arrows change letters  ·  Type A–Z  ·  Enter submits", bodyStyle(16)).setOrigin(0.5);

    const keyboard = this.input.keyboard!;
    keyboard.on("keydown-LEFT", () => {
      this.slot = (this.slot + 2) % 3;
      this.highlightSlot();
    });
    keyboard.on("keydown-RIGHT", () => {
      this.slot = (this.slot + 1) % 3;
      this.highlightSlot();
    });
    keyboard.on("keydown-UP", () => this.changeLetter(1));
    keyboard.on("keydown-DOWN", () => this.changeLetter(-1));
    keyboard.on("keydown-ENTER", () => {
      void this.submit();
    });
    keyboard.on("keydown", (event: KeyboardEvent) => {
      if (/^[a-zA-Z]$/.test(event.key)) {
        this.letters = replaceInitialAt(this.letters, this.slot, event.key);
        this.slotTexts[this.slot]?.setText(this.letters[this.slot] ?? "A");
        this.slot = Math.min(2, this.slot + 1);
        this.highlightSlot();
      }
    });
  }

  private skipSave(): void {
    this.pendingScore = null;
    this.status.setText("Score not saved. Choose Play Again or Menu.");
    this.enableNav();
  }

  private changeLetter(direction: 1 | -1): void {
    const current = this.letters[this.slot] ?? "A";
    this.letters = replaceInitialAt(this.letters, this.slot, stepLetter(current, direction));
    this.slotTexts[this.slot]?.setText(this.letters[this.slot] ?? "A");
    audioManager.playSfx("ui-move");
  }

  private highlightSlot(): void {
    this.slotTexts.forEach((text, index) => {
      text.setColor(index === this.slot ? "#8a4b12" : "#2a1a10");
      text.setScale(index === this.slot ? 1.08 : 1);
    });
  }

  private async submit(): Promise<void> {
    if (this.submitting || this.pendingScore === null) {
      return;
    }
    this.submitting = true;
    const initials = normalizeInitials(this.letters.join(""));
    this.status.setText("Sending your score…");
    const result = await submitHighScore(initials, this.pendingScore);
    if (!this.scene.isActive()) {
      return;
    }
    if (result.retained) {
      this.pendingScore = null;
      const rankText = result.rank ? `Rank ${result.rank}!` : "Saved.";
      this.status.setText(
        result.source === "local-fallback"
          ? `${rankText} ${result.notice ?? "Saved locally — not production data."}`
          : `${rankText} On the shared hibernation board.`,
      );
      this.time.delayedCall(700, () => {
        if (this.scene.isActive()) {
          this.scene.start("HighScores", { you: initials });
        }
      });
      return;
    }
    this.status.setText(`${result.error ?? "Could not save."} Press Submit to retry.`);
    this.submitting = false;
  }

  private playCelebrateFx(): void {
    if (this.anims.exists("new-high-score-celebration")) {
      const fx = this.add.sprite(GAME_WIDTH / 2, 240, "new-high-score-celebration", 0);
      fx.setScale(0.7).play("new-high-score-celebration");
    }
  }
}
